import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

// Layout
import { MainLayout } from "./components/Layout/MainLayout";

// Pages
import { Dashboard } from "./components/Dashboard/Dashboard";
import { InvoiceList } from "./components/Invoice/InvoiceList";
import { CreateInvoice } from "./pages/Invoice/CreateInvoice";
import { EditInvoice } from "./pages/Invoice/EditInvoice";
import { InvoiceDetail } from "./pages/Invoice/InvoiceDetail";
import { ClientListPage } from "./components/Client/ClientList";
import { CreateClient } from "./pages/Client/CreateClient";
import { EditClient } from "./pages/Client/EditClient";
import { ClientDetail } from "./pages/Client/ClientDetail";
import { ItemList } from "./components/Item/ItemList";
import { CreateItem } from "./pages/Item/CreateItem";
import { EditItem } from "./pages/Item/EditItem";
import { TemplateList } from "./components/Template/TemplateList";
import { TemplateCustomization } from "./pages/Template/TemplateCustomization";
import { Settings } from "./pages/Settings/Settings";
// import { ReportDashboard } from "./pages/Reports/ReportDashboard";

// Auth Context
import { AuthProvider } from "./context/AuthContext";
import { CreateTemplate } from "./pages/Template/CreateTemplete";
import InvoicePaymentSchedulePage from "./pages/InvoicePaymentSchedule";
import { LoginForm } from "./components/login/LoginForm";
import { User } from "./components/user/User";
import { Edition } from "./components/edition/Edition";
import { Tenant } from "./components/tenant";
import { SubscriptionPlan } from "./components/subscriptions/subscription-plan";
import { Role } from "./components/role/Role";
import { TenantPaymentSettingsPage } from "./pages/TenantPaymentSettings/TenantPaymentSettingsPage";
import { getPermissionByUserAndRole } from "./service/permission/permissionService";
import { useDispatch, useSelector } from "react-redux";
import { setPermissions } from "./store/slices/permissionSlice";
import { PermissionVariables } from "./shared/constant/PermissionVariables";
import SignUp from "./pages/SignUp/SignUp";
import { ForgotPassword } from "./pages/ForgetPassword/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword/ResetPassword";
import { hideLoader, showLoader } from "./store/slices/loadingSlice";
import PermissionDenied from "./pages/PermissionDenied/PermissionDenied";
import PaymentStatus from "./pages/PaymentStatus/PaymentStatus";
import { EmailSettings } from "./pages/EmailSettings/EmailSettings";
import { MailTemplate } from "./pages/MailTemplate/MailTemplate";
import AiAgent from "./pages/AiAgent/AiAgent";
import { ReportDashboard } from "./pages/Reports/ReportDashboard";
import { Appearance } from "./pages/Appearance/Appearance";
import { Loader } from "./components/ui/Loader";
import { TokenManager } from "./utils/TokenManager";
import { getValidIntegerOrNull, isValidEditionId } from "./utils/storageUtils";


export const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const [ready, setReady] = useState(false);
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    const check = async () => {
      // Check if access token exists and is valid
      const token = localStorage.getItem("token");

      if (!token) {

        setReady(true);
        <Navigate to="/login" replace />
        return;
      }

      // Try to refresh token silently if expired
      const valid = await TokenManager.checkAndRedirectIfExpired();

      if (valid) setRedirect(true);
      setReady(true);
    };

    check();
  }, []);

  if (!ready) return null; // wait until check is done
  if (redirect) return <Navigate to="/" replace />;
  return <>{children}</>;
};
// ProtectedRoute.tsx
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem("token");


      if (!token) {
        setAllowed(false);
        return;
      }

      // Check access token expiry, refresh using cookie if needed
      const valid = await TokenManager.checkAndRedirectIfExpired();
      setAllowed(valid);
    };

    check();
  }, []);

  if (allowed === null) return null; // wait for check
  return allowed ? <>{children}</> : <Navigate to="/login" replace />;
};

// App main component
const AppContent = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user.userDetails.user);
  const edition = useSelector((state: any) => state.edition.editionId);
  const dispatch = useDispatch();
  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );

  useEffect(() => {
    const fetchPermissionsAndRunCode = async () => {
      const editionId = isValidEditionId();
      const tenantId = getValidIntegerOrNull("tenantId");
      const shouldCallApi =
        (editionId && editionId > 0 && tenantId && tenantId > 0) || tenantId === null;
      if (!shouldCallApi) {
        dispatch(setPermissions([]));
        dispatch(hideLoader());
        return;
      }
      const userId = localStorage.getItem("userId");
      const roleIds = localStorage.getItem("roleIds") || "[]";
      if (userId && roleIds?.length !== 0) {
        const permissionInputs = {
          userId: JSON.parse(userId),
          tenantId: tenantId,
          roleIds: JSON.parse(roleIds),
        };
        dispatch(showLoader());
        const permissions = await getPermissionByUserAndRole(
          dispatch,
          permissionInputs
        ); // Wait for getPermission to complete

        dispatch(setPermissions(permissions));
        dispatch(hideLoader());
      }


    };

    fetchPermissionsAndRunCode();
  }, [edition,dispatch]);

  // useEffect(() => {
  //   const isLoggedIn = localStorage.getItem("isLoggedIn");
  //   console.log("isLoggedIn:", isLoggedIn);
  //   if (isLoggedIn == "true") {
  //     <Navigate to="/" replace />;
  //   } else {
  //     <Navigate to="/login" replace />;
  //   }
  // }, []);




  // Don't render routes until permissions are loaded
  if (userPermissions?.length === 0 && user && user?.length > 0 && isValidEditionId()) {
    return <Loader />
  }



  return (
    <Routes>
      {/* Public routes */}
      {/* <Route path="/login" element={<LoginForm />} /> */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginForm />
        </PublicRoute>
      } />

      <Route path="/register" element={<SignUp />} />

      <Route path="/forgotPassword" element={<ForgotPassword />} />

      <Route path="/resetPassword/:email" element={<ResetPassword />} />

      <Route path="/permission-denied" element={<PermissionDenied />} />

      {/* Public payment result pages (Stripe success/cancel redirect targets).
          Must stay outside ProtectedRoute — the payer is usually the client,
          not a logged-in tenant user. */}
      <Route path="/payment/success" element={<PaymentStatus status="success" />} />
      <Route path="/payment/cancel" element={<PaymentStatus status="cancelled" />} />


      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={"/users"}
        element={
          <ProtectedRoute>
            <MainLayout>
              <User />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plan"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SubscriptionPlan />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={
          userPermissions?.includes(PermissionVariables.tenant)
            ? "/tenants"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <Tenant />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Role />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={
          userPermissions?.includes(PermissionVariables.edition)
            ? "/editions"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <Edition />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Invoice routes */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.invoices)
            ? "/invoices"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={
          userPermissions?.includes(PermissionVariables.createInvoice)
            ? "/invoice/create"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <CreateInvoice />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoice/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoice/edit/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EditInvoice />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Client routes */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.client)
            ? "/client"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Ai agent routes */}
      {/* <Route
        path={
          // userPermissions?.includes(PermissionVariables.client)
          //   ? 
            "/askAi"
            // : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiAgent />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}

      <Route
        path={
          userPermissions?.includes(PermissionVariables.addClient)
            ? "/client/create"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <CreateClient />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/client/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={
          userPermissions?.includes(PermissionVariables.editClient)
            ? "/client/edit/:id"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <EditClient />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Item routes */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.items)
            ? "/item"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={
          userPermissions?.includes(PermissionVariables.addItem)
            ? "/item/create"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <CreateItem />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/item/edit/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EditItem />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Template routes */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.templates)
            ?
            "/template"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <TemplateList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={
          userPermissions?.includes(PermissionVariables.createTemplate)
            ? "/template/create"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <CreateTemplate />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/template/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TemplateCustomization />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings route */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.settings)
            ? "/settings"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <Settings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Mail Settings route */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.settings)
            ? "/mailSettings"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmailSettings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Mail Template Settings route */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.settings)
            ? "/mailTemplateSettings"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <MailTemplate />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Appearance (theme) route — available to every signed-in user */}
      <Route
        path="/appearance"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Appearance />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Reports route */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.reports)
            ? "/report"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReportDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Invoice Payment Schedule route */}
      <Route
        path={
          userPermissions?.includes(PermissionVariables.paymentSchedule)
            ? "/paymentSchedule"
            : "/dashboard"
        }
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoicePaymentSchedulePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App component with providers
const App = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      <AuthProvider>

        <AppContent />
      </AuthProvider>
    </div>
  );
};

export default App;
